import { useEffect } from 'react';

/**
 * Lightweight inline modal — no dep. Rendered as an overlay when the server
 * reports a successful group join on verify-email / Google sign-in / invite
 * accept.
 *
 * Call `onDismiss` from the OK button AND the backdrop click / Escape key.
 */
export function JoinedGroupModal({
  name,
  onDismiss,
}: {
  name: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    // Prevent background page scroll on mobile while the dialog is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onDismiss]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="joined-group-title"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Respect notch / home-indicator safe areas on iOS while keeping a
        // guaranteed 16px horizontal gutter so the dialog never kisses the
        // edge of a 320px viewport.
        padding:
          'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[360px] p-5 sm:p-6"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--fg-muted)',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Welcome
          </div>
          <h2
            id="joined-group-title"
            className="break-words"
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            You have joined <span style={{ color: 'var(--hot)' }}>{name}</span>.
          </h2>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          autoFocus
          className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 font-medium text-[color:var(--color-accent-fg)]"
        >
          OK
        </button>
      </div>
    </div>
  );
}
